import { useEffect, useState } from 'react';
import { AvatarPicker } from '../components/AvatarPicker';
import { AppearancePicker } from '../theme/Appearance';
import { api, type ApiError } from '../api/client';
import { Alert } from '../components/Field';
import { useAuth } from '../auth/AuthContext';

interface SessionRow {
  id: string;
  device: string | null;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export function Profile() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function load() {
    api<{ items: SessionRow[] }>('/auth/sessions')
      .then((result) => setSessions(result.items))
      .catch((caught) => setError(caught as ApiError));
  }

  useEffect(load, []);

  async function revokeAll() {
    try {
      const result = await api<{ revoked: number }>('/auth/logout-all', { method: 'POST' });
      setNotice(`${result.revoked} session(s) revoked. You will be signed out shortly.`);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Profile &amp; sessions</h1>
        <p className="page__lede">{user?.email}</p>
      </div>

      {error ? <Alert kind="error" title={error.message} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card__head">
          <span className="card__title">Account</span>
        </div>
        <div className="card__body">
          <table className="grid">
            <tbody>
              <tr>
                <td style={{ width: 200 }} className="muted">
                  Name
                </td>
                <td>
                  {user?.firstName} {user?.lastName}
                </td>
              </tr>
              <tr>
                <td className="muted">Company</td>
                <td>{user?.tenantName}</td>
              </tr>
              <tr>
                <td className="muted">Roles</td>
                <td>
                  {user?.roles.map((role) => (
                    <span className="pill pill--role" key={role}>
                      {role}
                    </span>
                  ))}
                </td>
              </tr>
              <tr>
                <td className="muted">Permissions</td>
                <td>{user?.permissions.length}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card__head">
          <span className="card__title">Profile picture</span>
        </div>
        <div className="card__body">
          <AvatarPicker />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card__head">
          <span className="card__title">Appearance</span>
        </div>
        <div className="card__body">
          <p className="muted" style={{ marginTop: 0 }}>
            Choose how SkillXzen looks for you. Changes apply as you pick them and are kept on
            this device — they do not affect anyone else.
          </p>
          <AppearancePicker layout="full" />
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <span className="card__title">Active sessions</span>
          <button className="btn btn--ghost btn--sm" onClick={() => void revokeAll()}>
            Sign out everywhere
          </button>
        </div>
        {!sessions ? (
          <div className="spinner">Loading sessions…</div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Device</th>
                <th>IP address</th>
                <th>Signed in</th>
                <th>Last used</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>
                  <td>{session.device ?? 'Browser'}</td>
                  <td className="muted">{session.ip ?? '—'}</td>
                  <td>{new Date(session.createdAt).toLocaleString()}</td>
                  <td className="muted">
                    {session.lastUsedAt ? new Date(session.lastUsedAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
